import { useCallback, useEffect, useRef, useState } from "react";
import { readUserCache, writeUserCache } from "../lib/dashboardCache";
import { markAdminFetched } from "../lib/adminPrefetch";

// Generic stale-while-revalidate fetcher for any GET-backed dashboard view
// (admin tabs, AI models, ...). Owns the GET lifecycle only — mutations,
// filters, and pagination stay in each caller hook and compose through
// refetch()/mutate()/cacheResource. See docs/architecture/frontend-data-fetching.md.
//
// Fetches come in two kinds with different UX:
//   - foreground (queryKey change, explicit refetch): sets isFetching so
//     pages can dim stale rows; surfaces errors.
//   - background (mount revalidate when seeded from cache, focus refetch,
//     the visible-only interval): silent — rows just swap; errors are
//     swallowed while there is data to show.

export type CachedQueryStatus = "loading" | "ready" | "error";

export type UseCachedQueryOptions<T> = {
  // User-scoped cache resource for the default view; null = network-only
  // (non-default filters), keeping previous data on screen while loading.
  cacheResource: string | null;
  queryKey: string;
  fetcher: (signal: AbortSignal) => Promise<T>;
  // 0 disables the dwell interval (e.g. while deep-paginated).
  refreshIntervalMs?: number;
  // Trims what gets persisted (not what's rendered), e.g. first 100 rows.
  persistTransform?: (value: T) => T;
};

export type UseCachedQueryResult<T> = {
  status: CachedQueryStatus;
  data: T | null;
  error: string | null;
  isFetching: boolean;
  refetch: () => Promise<void>;
  mutate: (updater: (prev: T) => T) => void;
};

export const REFRESH_INTERVAL_MS = 30_000;
export const FOCUS_THROTTLE_MS = 10_000;

export const shouldRefetchOnFocus = (lastSuccessAt: number, now: number): boolean =>
  now - lastSuccessAt > FOCUS_THROTTLE_MS;

type FetchKind = "foreground" | "background";

export const useCachedQuery = <T>(options: UseCachedQueryOptions<T>): UseCachedQueryResult<T> => {
  const { cacheResource, queryKey, refreshIntervalMs = REFRESH_INTERVAL_MS } = options;

  const [data, setData] = useState<T | null>(() =>
    cacheResource !== null ? readUserCache<T>(cacheResource) : null,
  );
  const [status, setStatus] = useState<CachedQueryStatus>(data !== null ? "ready" : "loading");
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const dataRef = useRef<T | null>(data);
  const seededRef = useRef<boolean>(data !== null);
  const firstRunRef = useRef<boolean>(true);
  const reqSeq = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);
  const abortRef = useRef<AbortController | null>(null);
  const lastSuccessAt = useRef<number>(0);

  // Callers pass inline closures; route them through refs so runFetch stays
  // stable and the effects below don't churn every render.
  const fetcherRef = useRef(options.fetcher);
  fetcherRef.current = options.fetcher;
  const persistRef = useRef(options.persistTransform);
  persistRef.current = options.persistTransform;
  const cacheResourceRef = useRef(cacheResource);
  cacheResourceRef.current = cacheResource;

  const runFetch = useCallback(async (kind: FetchKind): Promise<void> => {
    // Background ticks never preempt an in-flight fetch; a foreground fetch
    // always supersedes (newer filter wins).
    if (kind === "background" && inFlightRef.current) return;
    const mySeq = ++reqSeq.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    inFlightRef.current = true;
    if (kind === "foreground") {
      setIsFetching(true);
      setError(null);
      if (dataRef.current === null) setStatus("loading");
    }
    try {
      const body = await fetcherRef.current(controller.signal);
      if (mySeq !== reqSeq.current) return;
      dataRef.current = body;
      setData(body);
      setStatus("ready");
      setError(null);
      lastSuccessAt.current = Date.now();
      const resource = cacheResourceRef.current;
      if (resource !== null) {
        const transform = persistRef.current;
        writeUserCache(resource, transform !== undefined ? transform(body) : body);
        markAdminFetched(resource);
      }
    } catch (err) {
      if (controller.signal.aborted || mySeq !== reqSeq.current) return;
      if (kind === "foreground" || dataRef.current === null) {
        setError(err instanceof Error ? err.message : String(err));
        setStatus("error");
      }
    } finally {
      if (mySeq === reqSeq.current) {
        inFlightRef.current = false;
        if (kind === "foreground") setIsFetching(false);
      }
    }
  }, []);

  useEffect(() => {
    // The mount revalidate counts as background when seeded from cache so a
    // warm render never shows a loading state; everything after the first
    // run is a real query change and fetches in the foreground.
    const kind: FetchKind = firstRunRef.current && seededRef.current ? "background" : "foreground";
    firstRunRef.current = false;
    void runFetch(kind);
  }, [queryKey, runFetch]);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    if (refreshIntervalMs <= 0) return;
    let intervalId: number | null = null;
    const stop = (): void => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };
    const start = (): void => {
      if (intervalId === null) {
        intervalId = window.setInterval((): void => {
          if (document.visibilityState === "visible") void runFetch("background");
        }, refreshIntervalMs);
      }
    };
    const onWake = (): void => {
      if (document.visibilityState !== "visible") {
        stop();
        return;
      }
      if (shouldRefetchOnFocus(lastSuccessAt.current, Date.now())) void runFetch("background");
      start();
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    if (document.visibilityState === "visible") start();
    return (): void => {
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      stop();
    };
  }, [refreshIntervalMs, runFetch]);

  const refetch = useCallback((): Promise<void> => runFetch("foreground"), [runFetch]);

  // Local patch + write-through, for mutations that already know the next
  // state (delete/update responses) — keeps the persisted default view in
  // sync with what's on screen without a refetch round-trip. Before the
  // first list has landed there is nothing to patch, so fall back to a
  // fetch — the fresh list already reflects the mutation.
  const mutate = useCallback((updater: (prev: T) => T): void => {
    const prev = dataRef.current;
    if (prev === null) {
      void runFetch("foreground");
      return;
    }
    const next = updater(prev);
    // A mutation response supersedes any GET that started before it.
    reqSeq.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    inFlightRef.current = false;
    setIsFetching(false);
    setStatus("ready");
    setError(null);
    dataRef.current = next;
    setData(next);
    const resource = cacheResourceRef.current;
    if (resource !== null) {
      const transform = persistRef.current;
      writeUserCache(resource, transform !== undefined ? transform(next) : next);
    }
  }, [runFetch]);

  return { status, data, error, isFetching, refetch, mutate };
};
